import { useCallback, useRef, useState } from 'react';
import { CHAT_TYPE } from '../../../../utils/constants';
import {
    createDqAiSession,
    postDqAiMessage,
    fetchDqAiSession,
} from '../../../../services/dq_ai_chatbot.service';

/**
 * DQ AI chatbot preview hook (non-streaming JSON turns).
 * Owns: chat history, session id, collected-fields view, sending state.
 * The page always starts BLANK — a session (and its unique code) is created only
 * when the tester clicks "New chat", and an existing session is loaded only when
 * a code is pasted into Resume. No automatic restore.
 */

export const useDqAiChatbot = () => {
    const [sessionId, setSessionId] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [collectedView, setCollectedView] = useState(null);
    const [stage, setStage] = useState(null);
    const [pendingField, setPendingField] = useState(null);
    const [replyLanguage, setReplyLanguage] = useState('English');
    const [done, setDone] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const busyRef = useRef(false);

    const applyTurnData = useCallback((data) => {
        if (data.collected_view) setCollectedView(data.collected_view);
        if (data.stage) setStage(data.stage);
        setPendingField(data.pending_confirmation || null);
        if (data.reply_language) setReplyLanguage(data.reply_language);
        setDone(Boolean(data.done));
    }, []);

    const startNewSession = useCallback(async () => {
        setErrorText(null);
        setIsSending(true);
        try {
            const data = await createDqAiSession();
            setSessionId(data.session_id);
            setChatHistory([{ type: CHAT_TYPE.BOT, text: data.greeting_markdown }]);
            applyTurnData(data);
        } catch (e) {
            setErrorText(`Could not start a session: ${e.message}`);
        } finally {
            setIsSending(false);
        }
    }, [applyTurnData]);

    const resumeSession = useCallback(async (code) => {
        const sid = String(code || '').trim();
        if (!sid) return false;
        setErrorText(null);
        setIsSending(true);
        try {
            const data = await fetchDqAiSession(sid);
            setSessionId(data.session_id);
            setChatHistory(data.messages.map((m) => ({
                type: m.role === 'user' ? CHAT_TYPE.USER : CHAT_TYPE.BOT,
                text: m.text,
            })));
            applyTurnData(data);
            return true;
        } catch (e) {
            setErrorText(e.status === 404
                ? `Session not found: ${sid}` : `Could not resume: ${e.message}`);
            return false;
        } finally {
            setIsSending(false);
        }
    }, [applyTurnData]);

    const sendMessage = useCallback(async (text) => {
        const message = String(text || '').trim();
        if (!message || busyRef.current || !sessionId) return;
        busyRef.current = true;
        setErrorText(null);
        setIsSending(true);
        setChatHistory((prev) => [...prev, { type: CHAT_TYPE.USER, text: message }]);
        try {
            const data = await postDqAiMessage({ session_id: sessionId, message });
            setChatHistory((prev) => [...prev, { type: CHAT_TYPE.BOT, text: data.reply_markdown }]);
            applyTurnData(data);
        } catch (e) {
            setChatHistory((prev) => [...prev, {
                type: CHAT_TYPE.BOT,
                text: `⚠️ ${e.message || 'Something went wrong'} - please send that again.`,
            }]);
        } finally {
            busyRef.current = false;
            setIsSending(false);
        }
    }, [sessionId, applyTurnData]);

    return {
        sessionId,
        chatHistory,
        collectedView,
        stage,
        pendingField,
        replyLanguage,
        done,
        isSending,
        errorText,
        startNewSession,
        resumeSession,
        sendMessage,
    };
};

export default useDqAiChatbot;
